import * as React from 'react';

import { branch, compose, renderComponent, withProps } from 'recompose';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import { isAuthenticatedUser } from 'js/lib/user';

import { LoadingSection } from '@coursera/cds-core';

import { shouldShowLegalConsolidationAuthFlow } from 'bundles/authentication-common/utils';
import useIsFromUnregisteredOnboarding from 'bundles/onboarding-common/hooks/useIsFromUnregisteredOnboarding';
import useEnterpriseTermsOfUseModal from 'bundles/user-consent/hooks/useEnterpriseTermsOfUseModal';
import useMarketingConsentData from 'bundles/user-consent/hooks/useMarketingConsentData';

const LoadableGDPRPage = createLoadableComponent(() => import('bundles/user-consent/components/GDPRPage'));
const LoadableCCPAPage = createLoadableComponent(() => import('bundles/user-consent/components/CCPAPage'));

type MarketingConsentProps = {
  shouldShowCCPAPage: boolean;
  shouldShowGDPRPage: boolean;
  isEnterpriseAdmin: boolean;
  shouldShowMarketingConsent: boolean;
};

type LoadingProps = {
  isLoading: boolean;
};

function useConsentEligibility() {
  const { isFromUnregisteredOnboardingSignupFlow } = useIsFromUnregisteredOnboarding();

  const isInLegalConsolidationAuthFlow = shouldShowLegalConsolidationAuthFlow(isFromUnregisteredOnboardingSignupFlow);
  const shouldSkipMarketingConsentQuery = !isAuthenticatedUser() || isInLegalConsolidationAuthFlow;

  const {
    shouldShowCCPAMarketingConsent,
    shouldShowGDPRMarketingConsent: shouldShowGDPRPage,
    isEnterpriseAdmin,
    isLoading: isMarketingConsentDataLoading,
  } = useMarketingConsentData(undefined, shouldSkipMarketingConsentQuery);

  const shouldShowCCPAPage = !isInLegalConsolidationAuthFlow && shouldShowCCPAMarketingConsent;
  const isMarketingConsentRequired = shouldShowCCPAPage || shouldShowGDPRPage;

  const shouldSkipLfoQuery = shouldSkipMarketingConsentQuery || !isMarketingConsentRequired;

  const { isEnabled: isEnterpriseTermsOfUseModalEnabledForOrg, loading: isEnterpriseTermsOfUseModalEnabledLoading } =
    useEnterpriseTermsOfUseModal(shouldSkipLfoQuery);

  const shouldShowMarketingConsent = !isEnterpriseTermsOfUseModalEnabledForOrg && isMarketingConsentRequired;

  return {
    shouldShowMarketingConsent,
    shouldShowCCPAPage,
    shouldShowGDPRPage,
    isEnterpriseAdmin,
    isLoading: isMarketingConsentDataLoading || isEnterpriseTermsOfUseModalEnabledLoading,
  };
}

function MarketingConsent({
  shouldShowCCPAPage,
  shouldShowGDPRPage,
  isEnterpriseAdmin,
}: Omit<MarketingConsentProps, 'shouldShowMarketingConsent'>) {
  if (shouldShowCCPAPage) {
    return <LoadableCCPAPage isEnterpriseAdmin={isEnterpriseAdmin} />;
  } else if (shouldShowGDPRPage) {
    return <LoadableGDPRPage isEnterpriseAdmin={isEnterpriseAdmin} />;
  }

  return null;
}

/**
 * Higher-Order Component (HOC) that enhances a given page component with marketing consent eligibility logic.
 *
 * - Determines if the user should be shown a marketing consent page based on CCPA or GDPR requirements.
 * - Checks if the Enterprise Terms of Use modal is enabled and loading.
 * - Conditionally renders the appropriate consent page or the wrapped component.
 *
 * @param {React.ComponentType<MarketingConsentProps>} Component - The component to enhance.
 * @returns {React.ComponentType<P>} - The enhanced component with consent eligibility handling.
 */
export default (Component: React.ComponentType<MarketingConsentProps>) =>
  compose<MarketingConsentProps, {}>(
    withProps(() => {
      const eligibilityStatus = useConsentEligibility();
      return eligibilityStatus;
    }),
    branch<LoadingProps>(({ isLoading }) => !!isLoading, renderComponent(LoadingSection)),
    branch<Pick<MarketingConsentProps, 'shouldShowMarketingConsent'>>(
      ({ shouldShowMarketingConsent }) => !!shouldShowMarketingConsent,
      renderComponent(MarketingConsent)
    )
  )(Component);
